import type { Trip, TripStatus } from "@/entities/trip";

/**
 * Les offres de course faites à une Main — contrat du 2026-08-28.
 *
 * Module PUR → testable sans mock.
 *
 * ## La règle d'or, dont tout le reste découle
 * Trois canaux portent une offre, et ils n'ont pas le même statut :
 *
 *   • le SOCKET est instantané, mais ne garantit rien si l'app est fermée ;
 *   • la PUSH arrive app fermée, mais n'est JAMAIS rejouée — un échec est
 *     avalé, et « aucun destinataire » compte comme un succès ;
 *   • `GET /trips` est l'état vrai, à la demande.
 *
 * D'où la discipline : **le socket et la push sont des réveils, la liste est la
 * vérité.** On ne construit jamais l'état d'un écran à partir d'un événement
 * reçu — il dit « quelque chose a changé, va regarder », c'est la liste qui dit
 * quoi. Cette seule règle absorbe les doublons, les événements manqués, les
 * reconnexions et les notifications perdues.
 */

// ─── Distinguer une offre d'une course qui nous appartient ───────────────────

/**
 * États dans lesquels la course est RÉELLEMENT confiée à la Main qui lit.
 *
 * `GET /trips` renvoie l'UNION de deux ensembles : les courses attribuées, et
 * celles où une offre est en attente. Il faut donc les séparer soi-même.
 */
const ASSIGNED_STATUSES = new Set<TripStatus>([
  "AGENT_ASSIGNED",
  "AGENT_EN_ROUTE",
  "IN_PROGRESS",
  "AWAITING_CLIENT_APPROVAL",
  "DELIVERING",
]);

/**
 * Cette course est-elle une OFFRE qui m'est faite ?
 *
 * ⚠️ Le discriminant est `status` + `offer_expires_at`, et rien d'autre.
 * `agent_id` est un UUID agent-service, jamais le `sub` du jeton, et la réponse
 * ne dit nulle part lequel est le nôtre : s'en servir pour trancher échoue
 * silencieusement.
 */
export function isOffer(trip: Trip | null | undefined): boolean {
  return trip?.status === "AGENT_OFFER_PENDING" && trip.offer_expires_at != null;
}

/** Cette course m'est-elle réellement attribuée ? */
export function isAssignedTrip(trip: Trip | null | undefined): boolean {
  return trip?.status != null && ASSIGNED_STATUSES.has(trip.status);
}

// ─── L'échéance ──────────────────────────────────────────────────────────────

/**
 * Temps restant sur une offre, en millisecondes. Négatif une fois dépassée.
 *
 * ⚠️ TOUJOURS calé sur `expires_at`, l'échéance ABSOLUE — jamais sur
 * `timeout_minutes`, qui est le délai nominal accordé À L'ÉMISSION. Entre les
 * deux il y a la latence de diffusion : jusqu'à cinq secondes, plus le trajet
 * de la notification. Un décompte calé sur `timeout_minutes` surestime
 * toujours, et la Main croit avoir du temps alors que le refus l'attend.
 *
 * Rend `null` si l'échéance est absente ou illisible : mieux vaut n'afficher
 * aucun compte à rebours qu'un compte à rebours faux.
 */
export function offerRemainingMs(
  expiresAt: string | null | undefined,
  now: number,
): number | null {
  if (!expiresAt) return null;
  const at = Date.parse(expiresAt);
  return Number.isFinite(at) ? at - now : null;
}

/**
 * L'offre est-elle expirée ?
 *
 * ⚠️ À vérifier AVANT d'ouvrir un écran d'offre, quelle que soit la source.
 * Aucun TTL n'est posé sur les notifications : FCM applique son défaut de
 * QUATRE SEMAINES. Une push « il vous reste 5 minutes » peut donc arriver le
 * lendemain.
 *
 * Une échéance absente compte comme expirée : sans elle, rien ne permet
 * d'affirmer que l'offre est encore ouverte.
 */
export function isOfferExpired(
  expiresAt: string | null | undefined,
  now: number,
): boolean {
  const remaining = offerRemainingMs(expiresAt, now);
  return remaining == null || remaining <= 0;
}

/** Compte à rebours `mm:ss`, ou `null` si l'échéance est inexploitable. */
export function formatOfferCountdown(
  expiresAt: string | null | undefined,
  now: number,
): string | null {
  const remaining = offerRemainingMs(expiresAt, now);
  if (remaining == null) return null;
  if (remaining <= 0) return "00:00";
  const totalSec = Math.floor(remaining / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// ─── Trier la liste ──────────────────────────────────────────────────────────

export type AgentTripSplit = {
  /** Offres encore ouvertes, la plus urgente d'abord. */
  offers:   Trip[];
  /** Courses réellement attribuées, la plus récente d'abord. */
  assigned: Trip[];
};

/**
 * Sépare l'union rendue par `GET /trips` en offres et courses attribuées.
 *
 * Les offres EXPIRÉES sont écartées : aucun signal n'arrive à leur expiration
 * (cf. `isOfferExpired`), et la liste peut en contenir une que le serveur n'a
 * pas encore nettoyée. L'afficher enverrait la Main répondre à une offre morte.
 */
export function splitAgentTrips(trips: Trip[] | null | undefined, now: number): AgentTripSplit {
  const offers: Trip[] = [];
  const assigned: Trip[] = [];

  for (const trip of trips ?? []) {
    if (isOffer(trip)) {
      if (!isOfferExpired(trip.offer_expires_at, now)) offers.push(trip);
      continue;
    }
    if (isAssignedTrip(trip)) assigned.push(trip);
  }

  // La plus urgente d'abord : c'est celle qui va disparaître en premier.
  offers.sort(
    (a, b) =>
      (Date.parse(a.offer_expires_at ?? "") || 0) - (Date.parse(b.offer_expires_at ?? "") || 0),
  );
  assigned.sort(
    (a, b) => (Date.parse(b.created_at ?? "") || 0) - (Date.parse(a.created_at ?? "") || 0),
  );

  return { offers, assigned };
}

// ─── Le motif de refus ───────────────────────────────────────────────────────

export const DECLINE_REASON_MIN = 3;
export const DECLINE_REASON_MAX = 255;

export function normalizeDeclineReason(raw: string | undefined | null): string {
  return (raw ?? "").replace(/\s+/g, " ").trim().slice(0, DECLINE_REASON_MAX);
}

export function isUsableDeclineReason(raw: string | undefined | null): boolean {
  return normalizeDeclineReason(raw).length >= DECLINE_REASON_MIN;
}

/** Motifs proposés d'un geste — la Main répond souvent au guidon. */
export const DECLINE_REASON_SUGGESTIONS = [
  "Trop loin de ma position",
  "Je termine une autre course",
  "Pause / fin de service",
  "Problème de véhicule",
] as const;

// ─── Interpréter la réponse du serveur ───────────────────────────────────────

/**
 * Ce que l'app doit FAIRE d'une réponse à `accept` / `decline`.
 *
 *   • `ouvrir`   — l'offre est prise, on ouvre la course ;
 *   • `fermer`   — l'offre n'est plus à prendre : fermer l'écran et RECHARGER
 *                  la liste, sans présenter cela comme une panne ;
 *   • `corriger` — la saisie est en cause (motif trop court) ;
 *   • `reessayer`— panne réseau ou serveur, l'action reste possible.
 */
export type OfferResponseOutcome = "ouvrir" | "fermer" | "corriger" | "reessayer";

/**
 * Traduit un statut HTTP en conduite à tenir.
 *
 * ⚠️ Trois pièges que ce tableau désamorce :
 *
 * 1. **`403` n'est PAS une erreur d'authentification.** Une offre existe, mais
 *    elle est à quelqu'un d'autre — la rotation est passée à la Main suivante.
 *    Le traiter comme un problème de jeton (déconnexion, rafraîchissement) est
 *    une faute : c'est un état métier normal.
 *
 * 2. **`410` ne porte pas de code d'erreur spécifique** (`error.code` vaut
 *    `HTTP_ERROR`). Seul le statut HTTP fait foi.
 *
 * 3. **`409` couvre trois causes distinctes** sous le même `CONFLICT` — c'est
 *    le `message` du serveur qui les sépare, pas le code.
 */
export function offerResponseOutcome(httpStatus: number): OfferResponseOutcome {
  if (httpStatus >= 200 && httpStatus < 300) return "ouvrir";
  if (httpStatus === 409 || httpStatus === 403 || httpStatus === 410) return "fermer";
  if (httpStatus === 422) return "corriger";
  return "reessayer";
}

/**
 * Un `409` reçu après un `accept` doit-il être présenté comme un échec ?
 *
 * Non. `accept` n'accepte AUCUNE clé d'idempotence : un renvoi après coupure
 * réseau est indiscernable d'un doublon, et tombera en `409`. On le traite donc
 * comme un succès PROBABLE — on recharge la liste sans montrer d'erreur, et
 * c'est elle qui tranchera.
 *
 * Le remède en amont reste de désactiver le bouton dès le premier appui.
 */
export function isProbableDoubleAccept(httpStatus: number, hasRetried: boolean): boolean {
  return httpStatus === 409 && hasRetried;
}

// ─── Les deux « 409 » d'une acceptation ──────────────────────────────────────

/**
 * Pourquoi une acceptation a été refusée — §2 du contrat du 2026-09-02.
 *
 *   • `prise_ailleurs`  — une autre Main a été plus rapide. Depuis que l'offre
 *                         part à trois simultanément, c'est l'issue NORMALE
 *                         d'une course disputée, pas un incident.
 *   • `deja_occupee`    — la Main porte déjà une course. Rien à voir : elle
 *                         doit la terminer ou la rendre avant d'en prendre une
 *                         autre.
 *   • `expiree`         — les quatre-vingt-dix secondes sont passées pendant la
 *                         saisie.
 *   • `autre`           — refus qu'on ne sait pas nommer ; on affiche le
 *                         message du serveur tel quel.
 */
export type OfferRefusalKind = "prise_ailleurs" | "deja_occupee" | "expiree" | "autre";

/**
 * Sépare les causes que le statut HTTP confond.
 *
 * ⚠️ On discrimine sur le MESSAGE, faute de mieux : les deux `409` partagent le
 * même code `CONFLICT`, et le contrat ne donne aucun code métier pour les
 * distinguer. C'est fragile, et c'est assumé — d'où le repli `prise_ailleurs`,
 * qui est de loin le cas le plus fréquent et le moins dommageable à annoncer
 * par erreur.
 *
 * L'inverse ne serait pas vrai : dire « course déjà prise » à une Main qui
 * porte encore une course l'envoie attendre une offre qui ne viendra jamais,
 * puisque c'est elle-même qui se bloque.
 */
export function offerRefusalKind(
  httpStatus: number,
  serverMessage: string | undefined | null,
): OfferRefusalKind {
  if (httpStatus === 410) return "expiree";
  if (httpStatus !== 409 && httpStatus !== 403) return "autre";

  const m = (serverMessage ?? "").toLowerCase();
  // « Vous n'êtes plus disponible… » — le seul des deux qui appelle une action
  // de la part de la Main.
  if (m.includes("disponible")) return "deja_occupee";
  return "prise_ailleurs";
}

/** Titre et texte à afficher pour chaque refus, quand le serveur n'en fournit pas. */
export function offerRefusalCopy(kind: OfferRefusalKind): { title: string; message: string } {
  switch (kind) {
    case "prise_ailleurs":
      return {
        title: "Une autre Main a été plus rapide",
        message:
          "Cette course était proposée à plusieurs Mains en même temps. Ce n'est pas un incident — la prochaine arrive.",
      };
    case "deja_occupee":
      return {
        title: "Vous avez déjà une course",
        message:
          "Terminez-la ou rendez-la, puis vous pourrez en accepter une nouvelle.",
      };
    case "expiree":
      return {
        title: "Offre expirée",
        message: "Le délai est passé pendant la réponse. La course est repartie vers d'autres Mains.",
      };
    default:
      return {
        title: "Course indisponible",
        message: "Cette course n'est plus à prendre. En voici d'autres bientôt.",
      };
  }
}
